import { Injectable, signal } from '@angular/core';

export interface ToastMessage {
  id: string;
  variant: 'success' | 'error' | 'info';
  text: string;
  detail?: string;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  readonly messages = signal<ToastMessage[]>([]);

  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  success(text: string, detail?: string): void {
    this.addMessage('success', text, detail);
  }

  error(text: string, detail?: string): void {
    this.addMessage('error', text, detail);
  }

  info(text: string, detail?: string): void {
    this.addMessage('info', text, detail);
  }

  dismiss(id: string): void {
    this.clearTimer(id);
    this.messages.update((current) => current.filter((m) => m.id !== id));
  }

  clear(): void {
    for (const id of this.timers.keys()) {
      this.clearTimer(id);
    }

    this.messages.set([]);
  }

  private addMessage(variant: ToastMessage['variant'], text: string, detail?: string): void {
    const id = crypto.randomUUID();
    const message: ToastMessage = detail !== undefined
      ? { id, variant, text, detail }
      : { id, variant, text };

    this.messages.update((current) => [...current, message]);

    const timer = setTimeout(() => {
      this.dismiss(id);
    }, 4000);

    this.timers.set(id, timer);
  }

  private clearTimer(id: string): void {
    const existing = this.timers.get(id);
    if (existing !== undefined) {
      clearTimeout(existing);
      this.timers.delete(id);
    }
  }
}